import { CombinedGraphQLErrors, HttpLink, ServerError } from '@apollo/client';
import { ApolloLink } from '@apollo/client/link';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { DB_TYPE, ERRORS } from '@quesmed/types';
import { EProductType } from '@quesmed/types/models';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import { createClient } from 'graphql-ws';
import { EEnvironment, envVars } from '@/config';
import { toaster } from '@/core/ui';
import { ClientStorage, containsFileOrBlob } from '@/utils';
import { handleRefreshToken } from './handler/tokenRefresh';

const token = ClientStorage.cookies.getItem('ques-usertoken');
const environment = ClientStorage.local.getItem<EEnvironment>('ques-envMode') || EEnvironment.PRODUCTION;

// Determine which API URL to use based on environment
const _getApiUrl = () => {
  return environment === EEnvironment.DEVELOPMENT ? envVars.be.devApiUrl : envVars.be.apiUrl;
};

// Determine which GraphQL URL to use based on environment
const getGqlUrl = () => {
  return environment === EEnvironment.DEVELOPMENT ? envVars.be.devGqlLink : envVars.be.gqlLink;
};

const multipartLink = new UploadHttpLink({
  uri: getGqlUrl(),
  headers: {
    'Apollo-Require-Preflight': 'true',
    // 'Content-Type': 'multipart/form-data',
    // Accept: 'application/json',
    // Do NOT set 'Content-Type' here; apollo-upload-client will set it automatically for multipart requests
  },
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: envVars.be.subscriptionLink,
    connectionParams: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  }),
);

const httpLink = new HttpLink({
  uri: getGqlUrl(),
  // headers: {
  //   Authorization: token ? `Bearer ${token}` : "",
  // },
});

const authLink = new SetContextLink((prevContext, operation) => {
  const db = ClientStorage.session.getItem<DB_TYPE>('ques-dB');
  const token = ClientStorage.cookies.getItem('ques-usertoken');
  const mapping: Record<DB_TYPE, EProductType> = {
    [DB_TYPE.FINALS]: EProductType.QBANK,
    [DB_TYPE.MRCP]: EProductType.MRCP_PART1,
    [DB_TYPE.UCAT]: EProductType.UCAT,
  };

  const skip = operation.operationName === 'LoginAdmin';

  if (!db && !skip) {
    toaster.error('Please select a database');
    throw new Error('Please select a database');
  }

  const contextHeaders = prevContext.headers as { UserProduct?: EProductType } | undefined;
  const userProductOverride = contextHeaders?.UserProduct;

  return {
    ...prevContext,
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      UserProduct: userProductOverride ?? mapping[db || DB_TYPE.FINALS] ?? '',
      Database: db || DB_TYPE.FINALS,
    },
  };
});

const errorLink = new ErrorLink(({ operation, forward, error }) => {
  // const { variables, operationName } = operation || {};
  // const details = { operationName, variables };

  if (CombinedGraphQLErrors.is(error)) {
    for (const graphQLError of error.errors) {
      switch (graphQLError.message) {
        case ERRORS.TOKEN_INVALID: {
          handleRefreshToken(operation, forward);
          break;
        }

        default:
          toaster.error(graphQLError.message);
          break;
      }
    }
  } else if (ServerError.is(error)) {
    toaster.error(error.message);
  } else if (error) {
    toaster.error(error.message);
  }

  // if (networkError) {
  //   const { message } = networkError || {};
  //   const NETWORK_STATUS_CODE = /(4|5)[0-9]{2,2}/;
  //   if (NETWORK_STATUS_CODE.test(message)) {
  //     toaster.error('Something went wrong. If this problem persists, please let us know');
  //     return;
  //   }
  //   toaster.error('Check internet connection');
  // }
});

// Combined split link for handling subscriptions and file uploads
const splitLink = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink, // If it's a subscription, use wsLink
  ApolloLink.split(
    ({ variables }) => containsFileOrBlob(variables),
    multipartLink, // If it's a file upload, use multipartLink
    httpLink, // Otherwise, use httpLink
  ),
);

export const link = ApolloLink.from([authLink, errorLink, splitLink]);
